import Label from "@/v2-ui/label"
import Textarea from "@/v2-ui/textarea"
import TranslationAttributesHint from "@/v2-backoffice-client/translation/attributes/TranslationAttributesHint"
import { parseAttributes } from "@/v2-backoffice-client/translation/attributes/translation.attributes.utils"
import type { TranslationRow } from "@/v2-backoffice-client/translation/translation.types"

export type TranslationAttributesFieldProps = {
  /** Raw draft text, kept as typed so half-written JSON survives a re-render. */
  value: string
  onChange(value: string): void
  /** Every value of the selected type set, for the shape hint. */
  siblingRows: TranslationRow[]
  disabled: boolean
}

/**
 * The Attributes document for a value being created — a plain JSON field rather
 * than the Monaco editor the expanded row uses.
 *
 * The row's editor is a save surface of its own, with its own Save button; here
 * the document is one field of a form the dialog saves as a whole, so a second
 * editor with a second Save button beside the dialog's own would be two ways to
 * commit the same value. A textarea is also what a prefilled skeleton wants: it
 * is three keys and no history, not a document to navigate.
 */
function TranslationAttributesField(props: TranslationAttributesFieldProps) {
  const { value, onChange, siblingRows, disabled } = props

  const parsed = parseAttributes(value)

  return (
    <div className="space-y-2">
      <Label htmlFor="add-row-attributes">Attributes</Label>
      <Textarea
        id="add-row-attributes"
        className="font-mono text-xs"
        rows={4}
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={!parsed.ok}
      />
      {parsed.ok ? (
        <TranslationAttributesHint siblingRows={siblingRows} />
      ) : (
        <p className="text-xs text-destructive">{parsed.error}</p>
      )}
    </div>
  )
}

export default TranslationAttributesField
